import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin } from 'lucide-react';
import { useFooterLegalLinks } from '../hooks/useFooterLegalLinks';
import { GlobalLanguageSwitcher } from './GlobalLanguageSwitcher';
import api from '../utils/api';

const Footer = () => {
  const { t } = useTranslation();
  // TREATX-OWNER-UAT-LANDING-CONTACT-01:
  // Landing/Public footer contact must come from the canonical Corporate
  // Identity SSOT. clinics@treatx.health was removed at Owner request.
  // We surface the SSOT general_email (fallback: support@treatx.health)
  // and support@treatx.health as the ONLY contact addresses. No new
  // authority is created — this is a subordinate read of
  // GET /api/public/corporate-identity.
  const [entity, setEntity] = useState(null);
  // LEGAL-PUBLIC-FOOTER-PROCESS-01 — canonical legal documents shown here
  // come ONLY from the `public_legal_footer` process binding (SSOT).
  const { items: legalDocs } = useFooterLegalLinks();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/public/corporate-identity');
        if (!cancelled) setEntity(r?.data || null);
      } catch { /* silent — footer renders sensible defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const generalEmail = (entity && entity.general_email) || 'info@treatx.health';
  return (
    <footer className="bg-secondary text-white mt-auto" data-testid="main-footer">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div>
              <span className="text-2xl font-bold tracking-tight">TreatX</span>
            </div>
            <p className="text-white/80 text-sm">
              {t('footer.tagline', 'Global Medical Tourism Platform')}
            </p>
            <p className="text-white/60 text-xs mt-2">
              treatx.health
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.quickLinks', 'Quick Links')}</h3>
            <div className="flex flex-col gap-2">
              <Link to="/treatments" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-treatments">
                {t('footer.treatments', 'Treatments')}
              </Link>
              <Link to="/about" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-about">
                {t('footer.aboutUs', 'About Us')}
              </Link>
              <Link to="/about#corporate-identity-block" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-company-info">
                {t('footer.companyInfo', 'Company Information / Şirket Bilgileri')}
              </Link>
              <Link to="/contact" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-contact">
                {t('footer.contact', 'Contact')}
              </Link>
              <Link to="/for-clinics" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-for-clinics">
                {t('footer.forClinics', 'For Clinics')}
              </Link>
            </div>
          </div>

          {/* Provider & Admin Access - P0 Fix */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.forProviders', 'For Providers')}</h3>
            <div className="flex flex-col gap-2">
              <Link to="/provider/register" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-provider-register">
                {t('footer.joinAsProvider', 'Join as Hospital / Clinic / Doctor')}
              </Link>
              <Link to="/clinic/login" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-clinic-login">
                {t('footer.clinicLogin', 'Clinic Admin Login')}
              </Link>
              <Link to="/treatx-admin/login" className="text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-treatx-admin">
                {t('footer.treatxAdmin', 'TreatX Admin')}
              </Link>
            </div>
          </div>

          {/* Legal — SSOT: canonical legal DOCUMENTS assigned to the
              `public_legal_footer` process (GET /api/public/legal/footer).
              No hard-coded functional/informational links live here. */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.legal', 'Legal')}</h3>
            <div className="flex flex-col gap-2" data-testid="footer-legal-links">
              {legalDocs.map((d) => (
                <a
                  key={d.content_key}
                  href={`/legal/${d.content_key}`}
                  data-testid={`footer-legal-doc-${d.content_key}`}
                  className="text-white/80 hover:text-white text-sm transition-colors"
                >
                  {d.title}
                </a>
              ))}
              {/* Operational (non-document) link to the canonical hash-free
                  Meta data-deletion instructions page. Plain anchor so the
                  browser URL stays /data-deletion (no HashRouter rewrite). */}
              <a
                href="/data-deletion"
                data-testid="footer-data-deletion"
                className="text-white/80 hover:text-white text-sm transition-colors"
              >
                {t('dataDeletion.title', 'User Data Deletion')}
              </a>
            </div>
            {/* TREATX-COOKIE-SETTINGS-FOOTER-ENTRY-01 — the published Cookie
                Policy (§7) promises a Cookie Settings link at the bottom of
                every page. This is NOT a legal document, so it is rendered
                outside the `public_legal_footer` document list and points at
                the existing canonical consent-preference route. */}
            <div className="mt-3 pt-3 border-t border-white/10">
              <Link
                to="/cookie-settings"
                className="text-white/80 hover:text-white text-sm transition-colors"
                data-testid="footer-cookie-settings"
              >
                {t('cookies.footerLink', 'Cookie Settings')}
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.contactUs', 'Contact Us')}</h3>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${generalEmail}`} className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-email-info">
                <Mail className="w-4 h-4" />
                {generalEmail}
              </a>
              <a href="mailto:support@treatx.health" className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors" data-testid="footer-email-support">
                <Mail className="w-4 h-4" />
                support@treatx.health
              </a>
            </div>
          </div>
        </div>

        {/* Payment marks — iyzico's documented merchant requirement is that
            the official "iyzico ile Öde" mark appears on the website. The file
            is the UNMODIFIED official SVG from the iyzico logo pack (checksums
            in public/brand/iyzico/PROVENANCE.md): fixed height, auto width, no
            recolour, no crop. It states WHO collects card payments; it claims
            no card-scheme acceptance that has not been verified. */}
        <div
          className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
          data-testid="footer-payment-marks"
        >
          <span className="text-white/60 text-xs" data-testid="footer-payment-marks-label">
            {t('footer.paymentMarks.label')}
          </span>
          <img
            src="/brand/iyzico/iyzico_ile_ode_horizontal_white.svg"
            alt={t('footer.paymentMarks.iyzicoAlt')}
            style={{ height: 26, width: 'auto' }}
            className="p-2 shrink-0 max-w-full"
            data-testid="footer-mark-iyzico"
          />
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/60 text-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p>TreatX © {new Date().getFullYear()} – {t('footer.tagline', 'Global Medical Tourism Platform')}</p>
              <p className="mt-1">treatx.health</p>
            </div>
            {/* TREATX-FIPO-F8_2_C — language switcher for Header-less
                routes. Reuses the same LanguageContext (D-B11). */}
            <div data-testid="footer-language-switcher">
              <GlobalLanguageSwitcher variant="footer" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;